import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, FileText, CreditCard, Star, AlertTriangle, Upload, Home, DollarSign } from 'lucide-react';
import { CheckOutData } from '../../types/guest';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { calculateTaxes as computeTaxBreakdown } from '../../utils/taxCalculations';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (checkOutData: CheckOutData) => void;
  guestName: string;
  roomNumber?: string;
  currency?: string;
  loading?: boolean;
  isTaxExempt?: boolean;
}

type DepartureMode = 'Normal' | 'Early' | 'Eviction' | 'No-Show';
type AmountMode = 'HT' | 'TTC';

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  guestName,
  roomNumber,
  currency,
  isTaxExempt = false,
  loading = false
}) => {
  const displayCurrency = currency || 'USD';

  const [checkOutData, setCheckOutData] = useState<CheckOutData>({
    checkOutDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    finalRoomNumber: roomNumber || '',
    invoiceAmount: 0,
    departureMode: 'Normal',
    notes: '',
    additionalCharges: 0,
    damageReport: '',
    satisfactionRating: 5,
    feedback: ''
  });

  const [amountMode, setAmountMode] = useState<AmountMode>('TTC');
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoicePreview, setInvoicePreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  // TTC already includes FPT (HT + TVA + FPT). Never add FPT on top of TTC (HP-100).
  const breakdown = computeTaxBreakdown(checkOutData.invoiceAmount || 0, amountMode, isTaxExempt);
  const taxBreakdown = {
    ht: breakdown.amountHT,
    fpt: breakdown.amountFPT,
    tva: breakdown.amountTVA,
    ttc: breakdown.amountTTC,
    total: breakdown.amountTTC,
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required: final room number
    if (!checkOutData.finalRoomNumber || checkOutData.finalRoomNumber.trim() === '') {
      newErrors.finalRoomNumber = 'Le numéro de chambre final est requis';
    }

    // Required: invoice amount
    if (!checkOutData.invoiceAmount || checkOutData.invoiceAmount <= 0) {
      newErrors.invoiceAmount = 'Le montant de la facture est requis et doit être supérieur à 0';
    }

    // Required: departure date
    if (!checkOutData.checkOutDate) {
      newErrors.checkOutDate = 'La date de départ est requise';
    } else {
      const checkOutDate = new Date(checkOutData.checkOutDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (checkOutDate < today) {
        newErrors.checkOutDate = 'La date de départ ne peut pas être dans le passé';
      }
    }

    // Required: departure mode
    if (!checkOutData.departureMode) {
      newErrors.departureMode = 'Le mode de départ est requis';
    }

    // Required: invoice file upload (Requirement 8.3)
    if (!invoiceFile) {
      newErrors.invoiceFile = 'Le scan ou la photo de la facture est requis';
    }

    if (checkOutData.additionalCharges && checkOutData.additionalCharges < 0) {
      newErrors.additionalCharges = 'Les frais supplémentaires ne peuvent pas être négatifs';
    }

    if (checkOutData.satisfactionRating && (checkOutData.satisfactionRating < 1 || checkOutData.satisfactionRating > 5)) {
      newErrors.satisfactionRating = 'La note doit être entre 1 et 5';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Add invoice file to checkout data
    const dataWithInvoice = {
      ...checkOutData,
      amountType: amountMode,
      invoiceFile: invoiceFile!
    };

    onConfirm(dataWithInvoice);
  };

  const handleInputChange = (field: keyof CheckOutData, value: any) => {
    setCheckOutData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAmountChange = (value: number) => {
    setCheckOutData(prev => ({ ...prev, invoiceAmount: value }));
    if (errors.invoiceAmount) {
      setErrors(prev => ({ ...prev, invoiceAmount: '' }));
    }
  };

  const handleAmountModeSwitch = (newMode: AmountMode) => {
    // Preserve total amount when switching modes
    const currentAmount = checkOutData.invoiceAmount || 0;
    if (currentAmount > 0) {
      const taxes = computeTaxBreakdown(currentAmount, amountMode, isTaxExempt);
      // When switching from HT to TTC, use the billed TTC (includes FPT)
      // When switching from TTC to HT, use the calculated HT
      const newAmount = newMode === 'TTC' ? taxes.amountTTC : taxes.amountHT;
      setCheckOutData(prev => ({ ...prev, invoiceAmount: newAmount }));
    }
    setAmountMode(newMode);
  };

  const handleInvoiceFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({ 
          ...prev, 
          invoiceFile: 'Format de fichier invalide. Utilisez JPG, PNG ou PDF.' 
        }));
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrors(prev => ({ 
          ...prev, 
          invoiceFile: 'Le fichier est trop volumineux. Taille maximale: 10MB.' 
        }));
        return;
      }

      setInvoiceFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setInvoicePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setInvoicePreview(null);
      }

      // Clear error
      if (errors.invoiceFile) {
        setErrors(prev => ({ ...prev, invoiceFile: '' }));
      }
    }
  };

  const handleRemoveInvoice = () => {
    setInvoiceFile(null);
    setInvoicePreview(null);
  };

  const renderStarRating = () => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleInputChange('satisfactionRating', star)}
            className={`p-1 transition-colors ${
              star <= (checkOutData.satisfactionRating || 0)
                ? 'text-yellow-400'
                : 'text-gray-300 hover:text-yellow-300'
            }`}
          >
            <Star className="w-6 h-6 fill-current" />
          </button>
        ))}
        <span className="ml-2 text-sm text-gray-600">
          {checkOutData.satisfactionRating || 0}/5
        </span>
      </div>
    );
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Check-out</h2>
            <p className="text-sm text-gray-600 mt-1">
              {guestName} {roomNumber && `- Chambre ${roomNumber}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Final Room Number - Requirement 8.1 */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Home className="w-4 h-4" />
              Numéro de chambre final *
            </label>
            <input
              type="text"
              value={checkOutData.finalRoomNumber || ''}
              onChange={(e) => handleInputChange('finalRoomNumber', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.finalRoomNumber ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ex: 101"
              required
            />
            {errors.finalRoomNumber && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {errors.finalRoomNumber}
              </p>
            )}
          </div>

          {/* Invoice Amount - Requirement 8.1 */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="w-4 h-4" />
              Montant de la facture *
            </label>
            
            {/* Amount mode toggle */}
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => handleAmountModeSwitch('HT')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  amountMode === 'HT'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Hors Taxes (HT)
              </button>
              <button
                type="button"
                onClick={() => handleAmountModeSwitch('TTC')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  amountMode === 'TTC'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Toutes Taxes Comprises (TTC)
              </button>
            </div>

            <input
              type="number"
              min="0"
              step="0.01"
              value={checkOutData.invoiceAmount || ''}
              onChange={(e) => handleAmountChange(parseFloat(e.target.value) || 0)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.invoiceAmount ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="0.00"
              required
            />
            {errors.invoiceAmount && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {errors.invoiceAmount}
              </p>
            )}

            {/* Tax breakdown display */}
            {checkOutData.invoiceAmount > 0 && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm">
                <p className="font-medium text-blue-900 mb-2">Détail des taxes:</p>
                <div className="space-y-1 text-blue-800">
                  <div className="flex justify-between">
                    <span>HT:</span>
                    <span className="font-medium">{taxBreakdown.ht.toFixed(2)} {displayCurrency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>FPT (4%):</span>
                    <span className="font-medium">{taxBreakdown.fpt.toFixed(2)} {displayCurrency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{isTaxExempt ? 'TVA (Exonéré):' : 'TVA (16%):'}</span>
                    <span className="font-medium">{isTaxExempt ? '0.00' : taxBreakdown.tva.toFixed(2)} {displayCurrency}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-blue-200 font-semibold text-blue-900">
                    <span>TTC (montant facturé):</span>
                    <span>{taxBreakdown.ttc.toFixed(2)} {displayCurrency}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Date de départ - Requirement 8.1 */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4" />
              Date et heure de départ *
            </label>
            <input
              type="datetime-local"
              value={checkOutData.checkOutDate || ''}
              onChange={(e) => handleInputChange('checkOutDate', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.checkOutDate ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            />
            {errors.checkOutDate && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {errors.checkOutDate}
              </p>
            )}
          </div>

          {/* Departure Mode - Requirement 8.2 */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4" />
              Mode de départ *
            </label>
            <select
              value={checkOutData.departureMode || 'Normal'}
              onChange={(e) => handleInputChange('departureMode', e.target.value as DepartureMode)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.departureMode ? 'border-red-300' : 'border-gray-300'
              }`}
              required
            >
              <option value="Normal">Normal</option>
              <option value="Early">Départ anticipé (Early Departure)</option>
              <option value="Eviction">Expulsion (Eviction)</option>
              <option value="No-Show">Départ sans passer par la réception (No-Show)</option>
            </select>
            {errors.departureMode && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {errors.departureMode}
              </p>
            )}
          </div>

          {/* Invoice Upload - Requirement 8.3 */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Upload className="w-4 h-4" />
              Scan ou photo de la facture *
            </label>
            
            {!invoiceFile ? (
              <div>
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">Cliquez pour télécharger</span> ou glissez-déposez
                    </p>
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG ou PDF (max. 10MB)</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                    onChange={handleInvoiceFileChange}
                  />
                </label>
              </div>
            ) : (
              <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-medium text-gray-900">{invoiceFile.name}</p>
                      <p className="text-sm text-gray-600">
                        {(invoiceFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveInvoice}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {invoicePreview && (
                  <div className="mt-3">
                    <img 
                      src={invoicePreview} 
                      alt="Aperçu de la facture" 
                      className="w-full h-48 object-contain border border-gray-200 rounded"
                    />
                  </div>
                )}
              </div>
            )}
            
            {errors.invoiceFile && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {errors.invoiceFile}
              </p>
            )}
          </div>

          {/* Frais supplémentaires */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <CreditCard className="w-4 h-4" />
              Frais supplémentaires ({displayCurrency})
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={checkOutData.additionalCharges || ''}
              onChange={(e) => handleInputChange('additionalCharges', parseFloat(e.target.value) || 0)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.additionalCharges ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="0.00"
            />
            {errors.additionalCharges && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {errors.additionalCharges}
              </p>
            )}
          </div>

          {/* Rapport de dommages */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <AlertTriangle className="w-4 h-4" />
              Rapport de dommages
            </label>
            <textarea
              value={checkOutData.damageReport || ''}
              onChange={(e) => handleInputChange('damageReport', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Décrire tout dommage constaté dans la chambre..."
            />
          </div>

          {/* Note de satisfaction */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
              <Star className="w-4 h-4" />
              Note de satisfaction
            </label>
            {renderStarRating()}
            {errors.satisfactionRating && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {errors.satisfactionRating}
              </p>
            )}
          </div>

          {/* Commentaires */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4" />
              Commentaires de l'invité
            </label>
            <textarea
              value={checkOutData.feedback || ''}
              onChange={(e) => handleInputChange('feedback', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Commentaires ou suggestions de l'invité..."
            />
          </div>

          {/* Notes internes */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4" />
              Notes internes
            </label>
            <textarea
              value={checkOutData.notes || ''}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Notes internes pour l'équipe..."
            />
          </div>

          {/* Résumé */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">Résumé du check-out</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Invité:</span>
                <span className="font-medium">{guestName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Chambre finale:</span>
                <span className="font-medium">{checkOutData.finalRoomNumber || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date de départ:</span>
                <span className="font-medium">
                  {checkOutData.checkOutDate 
                    ? format(new Date(checkOutData.checkOutDate), 'dd MMMM yyyy à HH:mm', { locale: fr })
                    : '-'
                  }
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Mode de départ:</span>
                <span className="font-medium">
                  {checkOutData.departureMode === 'Normal' && 'Normal'}
                  {checkOutData.departureMode === 'Early' && 'Départ anticipé'}
                  {checkOutData.departureMode === 'Eviction' && 'Expulsion'}
                  {checkOutData.departureMode === 'No-Show' && 'No-Show'}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-300">
                <span className="text-gray-600">Montant facturé (TTC, FPT inclus):</span>
                <span className="font-semibold text-blue-600">
                  {taxBreakdown.ttc.toFixed(2)} {displayCurrency}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Facture uploadée:</span>
                <span className={`font-medium ${invoiceFile ? 'text-green-600' : 'text-red-600'}`}>
                  {invoiceFile ? '✓ Oui' : '✗ Non'}
                </span>
              </div>
              {checkOutData.additionalCharges && checkOutData.additionalCharges > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Frais supplémentaires:</span>
                  <span className="font-medium text-orange-600">
                    {checkOutData.additionalCharges.toFixed(2)} {displayCurrency}
                  </span>
                </div>
              )}
              {checkOutData.satisfactionRating && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Satisfaction:</span>
                  <div className="flex items-center gap-1">
                    {[...Array(checkOutData.satisfactionRating)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-yellow-400 fill-current" />
                    ))}
                    <span className="ml-1 text-xs text-gray-500">
                      ({checkOutData.satisfactionRating}/5)
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              disabled={loading}
            >
              Annuler
            </button>
            
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Traitement...' : 'Confirmer le check-out'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Render modal in a portal attached to document.body
  return createPortal(modalContent, document.body);
};

export default CheckoutModal;
